import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { authenticatedFetch } from '../api';
import { DateOnlyInput } from '../components/DateOnlyInput';
import { Evidence, Choices } from './ConsultationForms';
export type Row = Record<string, any>;
export type Save = (body: Row, path?: string) => Promise<Row>;
export function Select({
  name,
  label,
  options,
  value,
  optional = false,
  onChange
}: {
  name: string;
  label: string;
  options: any[][];
  value?: string;
  optional?: boolean;
  onChange?: (v: string) => void;
}) {
  const choices=options.filter((option,index)=>options.findIndex(item=>String(item[0])===String(option[0]))===index);
  useEffect(() => {
    if (!optional && !value && options.length === 1 && onChange) onChange(options[0]![0]);
  }, [value, optional, options.length === 1 ? String(options[0]![0]) : '']);
  return (
    <label>
      {label}
      <select
        aria-label={label}
        name={name}
        required={!optional}
        defaultValue={value ?? (!optional && options.length === 1 ? options[0]![0] : '')}
        onChange={(e) => onChange?.(e.target.value)}
      >
        <option value="">Choose</option>
        {choices.map(([id, text]) => (
          <option key={id} value={id}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );
}
export function Text({
  name,
  label,
  value = '',
  optional = false,
  dateOnly = false,
  type = 'text'
}: {
  name: string;
  label: string;
  value?: string;
  optional?: boolean;
  dateOnly?: boolean;
  type?: string;
}) {
  return (
    <label>
      {label}
      {type === 'textarea' ? (
        <textarea aria-label={label} name={name} defaultValue={value} required={!optional} />
      ) : dateOnly ? (
        <DateOnlyInput name={name} defaultValue={value} required={!optional} />
      ) : (
        <input aria-label={label} name={name} type={type} defaultValue={value} required={!optional} />
      )}
    </label>
  );
}
export function Action({
  title,
  busy,
  documents,
  children,
  onSave,
  open = false
}: {
  title: string;
  busy: boolean;
  documents?: Row[];
  children?: ReactNode;
  onSave: (v: Row, key: string, form: FormData) => Promise<unknown>;
  open?: boolean;
}) {
  const [key, setKey] = useState(() => crypto.randomUUID());
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget),
      v = Object.fromEntries(form);
    try {
      await onSave(v, key, form);
      setKey(crypto.randomUUID());
    } catch {
      /* Parent keeps error and form values. */
    }
  }
  return (
    <details open={open || undefined}>
      <summary>{title}</summary>
      <form aria-label={title} onSubmit={submit}>
        <fieldset disabled={busy}>
          {children}
          {documents && <Evidence documents={documents} />}
          <Text name="reason" label="Review note" type="textarea" />
          <button>Save</button>
        </fieldset>
      </form>
    </details>
  );
}
export async function downloadConveyancing(path: string, name: string) {
  const r = await authenticatedFetch(path);
  if (!r.ok) throw Error('The document could not be downloaded. Review the current evidence and access.');
  const url = URL.createObjectURL(await r.blob()),
    a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
export function FactsForm({ setup, initial, busy, save }: { setup: Row; initial?: Row; busy: boolean; save: Save }) {
  const adopting = initial?.suite_version === 0,
    f = initial?.transaction_details,
    [property, setProperty] = useState(initial?.premises_id ?? (setup.properties.length === 1 ? setup.properties[0].id : '')),
    [kind, setKind] = useState(f?.type ?? 'Sale');
  const types = ['Sale', 'Remortgage', 'Lease extension', 'Lease variation', 'Staircasing', 'Subletting', 'Retrospective consent'];
  return (
    <Action
      title={adopting ? 'Integrate this existing conveyancing case' : initial ? 'Update transaction details' : 'Start a transaction'}
      busy={busy}
      onSave={async (v, key, form) =>
        save(
          {
            ...(adopting
              ? { services: form.getAll('services'), documentIds: form.getAll('documentIds') }
              : initial
                ? { action: 'Update transaction', version: initial.suite_version }
                : { building: setup.building, premisesId: v.property, caseId: v.caseId || null, services: form.getAll('services') }),
            facts: {
              type: kind,
              ownerId: v.ownerId,
              solicitorPersonId: v.solicitorPersonId || null,
              solicitorOrganisationId: v.solicitorOrganisationId || null,
              incomingPersonIds: form.getAll('incomingPersonIds'),
              incomingOrganisationId: v.incomingOrganisationId || null,
              outgoingInterestId: v.outgoingInterestId || null,
              targetDate: v.targetDate || null,
              jurisdiction: v.jurisdiction,
              notes: v.notes
            },
            reason: v.reason,
            idempotencyKey: key
          },
          adopting ? '/api/conveyancing/' + initial.id + '/adopt' : undefined
        )
      }
    >
      {!initial && <Select name="property" label="Property" value={property} options={setup.properties.map((p: Row) => [p.id, p.display_name])} onChange={setProperty} />}
      <Select name="type" label="Transaction type" value={kind} options={types.map((t) => [t, t])} onChange={setKind} />
      <Select name="ownerId" label="Transaction owner" value={f?.ownerId ?? setup.actorId} options={setup.users.map((u: Row) => [u.id, u.display_name])} />
      <Select name="jurisdiction" label="Property jurisdiction" value={f?.jurisdiction} options={['England', 'Wales'].map((t) => [t, t])} />
      <Select
        name="outgoingInterestId"
        label="Outgoing ownership"
        value={f?.outgoingInterestId}
        optional
        options={setup.interests.filter((i: Row) => i.premises_id === property).map((i: Row) => [i.id, i.official_owner_name])}
      />
      <Choices
        name="incomingPersonIds"
        title="Incoming leaseholders"
        initial={f?.incomingPersonIds ?? []}
        options={setup.people.map((p: Row) => ({ id: p.id, title: p.display_name }))}
      />
      <Select
        name="incomingOrganisationId"
        label="Incoming company, if applicable"
        value={f?.incomingOrganisationId}
        optional
        options={setup.organisations.map((o: Row) => [o.id, o.legal_name])}
      />
      <Select name="solicitorPersonId" label="Solicitor contact" value={f?.solicitorPersonId} optional options={setup.people.map((p: Row) => [p.id, p.display_name])} />
      <Select
        name="solicitorOrganisationId"
        label="Solicitor firm"
        value={f?.solicitorOrganisationId}
        optional
        options={setup.organisations.map((o: Row) => [o.id, o.legal_name])}
      />
      <Text name="targetDate" label="Target completion date" value={f?.targetDate} dateOnly optional />
      <Text name="notes" label="Transaction notes" value={f?.notes} type="textarea" optional />
      {(!initial || adopting) && (
        <>
          {!adopting && (
            <Select
              name="caseId"
              label="Existing correspondence case, if applicable"
              optional
              options={(setup.cases ?? []).map((c: Row) => [c.id, c.case_number + ' — ' + c.title])}
            />
          )}
          <Choices name="services" title="Required services" initial={[]} options={setup.serviceKinds.map((s: string) => ({ id: s, title: s }))} />
        </>
      )}
      {adopting && <Evidence documents={setup.documents.filter((d: Row) => d.selectable)} />}
    </Action>
  );
}
